import React, { useEffect, useState } from "react";
import { Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "../../lib/config";
import { toast } from "sonner";
import { LogOut, Compass } from "lucide-react";

export function AdminLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startLogin = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-owt-bg" data-testid="admin-login">
      <div className="max-w-md w-full border border-owt-border bg-owt-surface p-8 md:p-12 text-center">
        <Compass className="w-10 h-10 mx-auto text-owt-terracotta"/>
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-muted mt-4">Once Way · Studio</div>
        <h1 className="font-display text-4xl text-owt-forest mt-2">Founders' Console</h1>
        <p className="text-owt-muted mt-3">Sign in with Google to manage trips, blogs, testimonials and enquiries.</p>
        <button onClick={startLogin} data-testid="admin-google-login" className="mt-8 w-full bg-owt-forest text-owt-surface py-3 font-mono text-[11px] tracking-[0.25em] uppercase hover:bg-owt-terracotta transition-colors">Continue with Google</button>
      </div>
    </div>
  );
}

export function AuthCallback() {
  const [msg, setMsg] = useState("Signing you in...");
  const [denied, setDenied] = useState(false);
  const nav = useNavigate();
  useEffect(() => {
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sid = params.get("session_id");
    if (!sid) { nav("/admin/login"); return; }
    api.post("/auth/session", { session_id: sid }).then(() => {
      window.history.replaceState({}, "", "/admin");
      nav("/admin", { replace: true });
    }).catch((err) => {
      if (err?.response?.status === 403) {
        setDenied(true);
        setMsg("Access denied — this email is not authorized to access the admin portal.");
      } else {
        setMsg("Login failed. Please retry.");
        setTimeout(() => nav("/admin/login"), 1500);
      }
    });
  }, [nav]);
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-5 text-center gap-6" data-testid="auth-callback">
      <div className={`font-mono text-sm ${denied ? "text-owt-terracotta" : "text-owt-muted"}`} data-testid="auth-callback-message">{msg}</div>
      {denied && (
        <a href="/" className="font-mono text-[10px] tracking-[0.25em] uppercase text-owt-forest underline underline-offset-4" data-testid="auth-denied-home-link">Back to website</a>
      )}
    </div>
  );
}

export function ProtectedAdmin({ children }) {
  const [state, setState] = useState({ ready: false, ok: false, user: null });
  const loc = useLocation();
  useEffect(() => {
    if (window.location.hash.includes("session_id=")) { setState({ ready: true, ok: false, user: null }); return; }
    api.get("/auth/me")
      .then((r) => setState({ ready: true, ok: true, user: r.data }))
      .catch(() => setState({ ready: true, ok: false, user: null }));
  }, [loc.pathname]);
  if (!state.ready) return <div className="min-h-screen flex items-center justify-center font-mono text-sm text-owt-muted">Checking session...</div>;
  if (!state.ok) return <Navigate to="/admin/login" replace/>;
  return React.cloneElement(children, { user: state.user });
}

export function AdminLayout({ user, children, active }) {
  const nav = useNavigate();
  const logout = async () => {
    await api.post("/auth/logout");
    toast.success("Signed out");
    nav("/admin/login");
  };
  const NAV = [
    { to: "/admin", label: "Overview", key: "overview" },
    { to: "/admin/trips", label: "Trips", key: "trips" },
    { to: "/admin/blogs", label: "Blogs", key: "blogs" },
    { to: "/admin/testimonials", label: "Testimonials", key: "testimonials" },
    { to: "/admin/enquiries", label: "Enquiries", key: "enquiries" },
    { to: "/admin/clients", label: "Clients", key: "clients" },
    { to: "/admin/itineraries", label: "Itineraries", key: "itineraries" },
    { to: "/admin/access", label: "Access", key: "access" },
  ];
  return (
    <div className="min-h-screen grid md:grid-cols-[240px_1fr] bg-owt-bg">
      <aside className="border-r border-owt-border bg-owt-surface p-6 md:min-h-screen">
        <div className="font-display text-2xl text-owt-forest">Once Way</div>
        <div className="font-mono text-[9px] tracking-[0.25em] uppercase text-owt-muted mt-1">Founders' Console</div>
        <nav className="mt-8 space-y-1">
          {NAV.map((n) => (
            <Link key={n.key} to={n.to} data-testid={`admin-nav-${n.key}`} className={`block px-3 py-2 font-mono text-[11px] tracking-[0.15em] uppercase ${active === n.key ? "bg-owt-forest text-owt-surface" : "text-owt-forest hover:bg-owt-sand/40"}`}>{n.label}</Link>
          ))}
        </nav>
        <div className="mt-10 pt-6 border-t border-owt-border">
          {user && <div className="text-sm text-owt-forest">{user.name}</div>}
          <button onClick={logout} data-testid="admin-logout" className="mt-3 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-owt-muted hover:text-owt-terracotta"><LogOut className="w-3 h-3"/> Sign out</button>
        </div>
      </aside>
      <main className="p-6 md:p-10">{children}</main>
    </div>
  );
}
